import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const CaptureSetting = ({ themeId, themePageMaster }) => {
  // 촬영 페이지 상태
  const [capturePage, setCapturePage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    captureTimerSeconds: 10,
    captureCount: 4,
    captureCountTextPositionX: 0,
    captureCountTextPositionY: 0,
    captureCounterTextPositionX: 0,
    captureCounterTextPositionY: 0,
    aiModeDisplaySize: 50,
    aiModeDisplayColor: '#FFFFFF',
    resetButtonRedirectUrl: 'start'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchCapturePage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getCapturePage(themeId);
        
        setCapturePage({
          ...capturePage,
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
        // 에러 발생 시 기본 데이터 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchCapturePage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setCapturePage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setCapturePage({
      ...capturePage,
      [resourceType]: resourceId
    });
  };

  // 위치 변경 핸들러
  const handlePositionChange = (type, axis, value) => {
    const numValue = value === "" ? 0 : parseInt(value);
    
    if (type === 'countText') {
      setCapturePage(prev => ({
        ...prev,
        [`captureCountText${axis.charAt(0).toUpperCase() + axis.slice(1)}`]: numValue
      }));
    } else if (type === 'counterText') {
      setCapturePage(prev => ({
        ...prev,
        [`captureCounterText${axis.charAt(0).toUpperCase() + axis.slice(1)}`]: numValue
      }));
    }
  };

  // 방향 버튼 핸들러
  const handleDirectionClick = (type, direction) => {
    const step = 5;
    let newX, newY;

    if (type === 'countText') {
      newX = capturePage.captureCountTextPositionX || 0;
      newY = capturePage.captureCountTextPositionY || 0;
    } else if (type === 'counterText') {
      newX = capturePage.captureCounterTextPositionX || 0;
      newY = capturePage.captureCounterTextPositionY || 0;
    }

    switch (direction) {
      case 'up':
        newY -= step;
        break;
      case 'down':
        newY += step;
        break;
      case 'left':
        newX -= step;
        break;
      case 'right':
        newX += step;
        break;
    }

    if (type === 'countText') {
      setCapturePage(prev => ({
        ...prev,
        captureCountTextPositionX: newX,
        captureCountTextPositionY: newY
      }));
    } else if (type === 'counterText') {
      setCapturePage(prev => ({
        ...prev,
        captureCounterTextPositionX: newX,
        captureCounterTextPositionY: newY
      }));
    }
  };

  // 중앙 정렬 핸들러
  const handleCenterAlign = (type) => {
    const centerX = 500;
    const centerY = 450;
    
    if (type === 'countText') {
      setCapturePage(prev => ({
        ...prev,
        captureCountTextPositionX: centerX,
        captureCountTextPositionY: centerY
      }));
    } else if (type === 'counterText') {
      setCapturePage(prev => ({
        ...prev,
        captureCounterTextPositionX: centerX,
        captureCounterTextPositionY: centerY
      }));
    }
  };

  // 타이머 조절 핸들러
  const handleTimerDecrease = () => {
    setCapturePage(prev => ({
      ...prev,
      captureTimerSeconds: Math.max((prev.captureTimerSeconds || 10) - 1, 1)
    }));
  };

  const handleTimerIncrease = () => {
    setCapturePage(prev => ({
      ...prev,
      captureTimerSeconds: (prev.captureTimerSeconds || 10) + 1
    }));
  };

  // 촬영 횟수 조절 핸들러
  const handleCaptureCountDecrease = () => {
    setCapturePage(prev => ({
      ...prev,
      captureCount: Math.max((prev.captureCount || 4) - 1, 1)
    }));
  };

  const handleCaptureCountIncrease = () => {
    setCapturePage(prev => ({
      ...prev,
      captureCount: Math.min((prev.captureCount || 4) + 1, 8)
    }));
  };

  // AI 모드 크기 변경 핸들러
  const handleAIModeSizeChange = (value) => {
    const sizeValue = parseInt(value) || 50;
    setCapturePage(prev => ({
      ...prev,
      aiModeDisplaySize: sizeValue
    }));
  };

  // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
  useEffect(() => {
    const eventName = `savePageDesign_shooting_mode`; // 각 컴포넌트마다 고유한 이벤트 이름

    const saveHandler = async (event) => {
      try {
        // 저장 중임을 표시
        setIsLoading(true);

        // 자체 로직을 사용하여 데이터 저장
        const isSucceed = await designApi.saveCapturePage(event.detail.themeId, capturePage);

        if(isSucceed) {
          // 성공 메시지
          alert("저장되었습니다.");
        } else {
          // 실패 메시지
          alert("저장에 실패했습니다.");
        }

      } catch (error) {
        // 오류 처리
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener(eventName, saveHandler);

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [capturePage, themeId]); // 의존성 배열에 필요한 상태 포함

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={capturePage.languageCode || "ko"}
            onChange={(e) =>
              handleInputChange('languageCode', e.target.value)
            }
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={capturePage.backgroundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundResourceId", id)
          }
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={capturePage.backgroundMusicResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundMusicResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>

      {/* 스탑 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">스탑 버튼</h4>
        <ResourceUploadField
          label=""
          resourceId={capturePage.stopButtonResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("stopButtonResourceId", id)
          }
          acceptTypes="image/*"
        />
      </div>

      {/* 효과음 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">효과음</h4>
        <ResourceUploadField
          label="카운트 다운"
          resourceId={capturePage.countdownSoundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("countdownSoundResourceId", id)
          }
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="촬영"
          resourceId={capturePage.captureSoundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("captureSoundResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>

      {/* 사진촬영 옵션 섹션 */}
      {/* <div className="settings-box">
        <h4 className="settings-title">타이머</h4>
        <div className="settings-row">
          <div className="settings-label">타이머 시간</div>
          <div className="timer-control-container">
            <button 
              className="timer-btn" 
              onClick={handleTimerDecrease}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="timer-input" 
              value={capturePage.captureTimerSeconds || 10}
              onChange={(e) => handleInputChange('captureTimerSeconds', parseInt(e.target.value) || 1)}
            />
            <button 
              className="timer-btn"
              onClick={handleTimerIncrease}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="timer-unit">초</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">사진 촬영 횟수 *최대 8회</div>
          <div className="shooting-count-container">
            <button 
              className="shooting-count-btn decrease-btn"
              onClick={handleCaptureCountDecrease}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="shooting-count-input" 
              value={capturePage.captureCount || 4}
              onChange={(e) => handleInputChange('captureCount', parseInt(e.target.value) || 1)}
            />
            <button 
              className="shooting-count-btn increase-btn"
              onClick={handleCaptureCountIncrease}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">회</span>
          </div>
        </div>
      </div> */}

      {/* 사진촬영 횟수 및 카운트 위치 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">촬영 횟수 및 카운트 위치</h4>
        <div className="settings-row">
          <div className="settings-label">횟수</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input 
                type="text" 
                className="position-input" 
                value={capturePage.captureCountTextPositionX || 0}
                onChange={(e) => handlePositionChange('countText', 'positionX', e.target.value)}
              />
              <span className="position-label">Y</span>
              <input 
                type="text" 
                className="position-input" 
                value={capturePage.captureCountTextPositionY || 0}
                onChange={(e) => handlePositionChange('countText', 'positionY', e.target.value)}
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button 
                  className="direction-btn" 
                  data-direction="up"
                  onClick={() => handleDirectionClick('countText', 'up')}
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button 
                  className="direction-btn" 
                  data-direction="left"
                  onClick={() => handleDirectionClick('countText', 'left')}
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button 
                  className="direction-btn" 
                  data-direction="right"
                  onClick={() => handleDirectionClick('countText', 'right')}
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button 
                  className="direction-btn" 
                  data-direction="down"
                  onClick={() => handleDirectionClick('countText', 'down')}
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button 
              className="btn center-btn"
              onClick={() => handleCenterAlign('countText')}
            >
              중앙정렬
            </button>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">카운트</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input 
                type="text" 
                className="position-input" 
                value={capturePage.captureCounterTextPositionX || 0}
                onChange={(e) => handlePositionChange('counterText', 'positionX', e.target.value)}
              />
              <span className="position-label">Y</span>
              <input 
                type="text" 
                className="position-input" 
                value={capturePage.captureCounterTextPositionY || 0}
                onChange={(e) => handlePositionChange('counterText', 'positionY', e.target.value)}
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button 
                  className="direction-btn" 
                  data-direction="up"
                  onClick={() => handleDirectionClick('counterText', 'up')}
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button 
                  className="direction-btn" 
                  data-direction="left"
                  onClick={() => handleDirectionClick('counterText', 'left')}
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button 
                  className="direction-btn" 
                  data-direction="right"
                  onClick={() => handleDirectionClick('counterText', 'right')}
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button 
                  className="direction-btn" 
                  data-direction="down"
                  onClick={() => handleDirectionClick('counterText', 'down')}
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button 
              className="btn center-btn"
              onClick={() => handleCenterAlign('counterText')}
            >
              중앙정렬
            </button>
          </div>
        </div>
      </div>

      {/* AI 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">AI 모드 표시</h4>
        <div className="settings-row">
          <div className="settings-label">사이즈 조절</div>
          <div className="size-slider-container">
            <i className="ri-subtract-line size-icon small"></i>
            <input 
              type="range" 
              className="size-slider" 
              min="10" 
              max="100" 
              value={capturePage.aiModeDisplaySize || 50}
              onChange={(e) => handleAIModeSizeChange(e.target.value)}
            />
            <i className="ri-add-line size-icon large"></i>
            <span className="size-value">{capturePage.aiModeDisplaySize || 50}%</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={capturePage.aiModeDisplayColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={capturePage.aiModeDisplayColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('aiModeDisplayColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 리셋 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">리셋</h4>
        <div className="settings-row">
          <div className="settings-label">리셋 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={capturePage.resetButtonResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("resetButtonResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">버튼 클릭 시 이동 페이지</div>
          <div className="page-select-container">
            <select 
              className="page-select"
              value={capturePage.resetButtonRedirectUrl || "start"}
              onChange={(e) => handleInputChange('resetButtonRedirectUrl', e.target.value)}
            >
              <option value="start">시작화면</option>
              <option value="capture_standby">촬영대기</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptureSetting;